/* global $ */

import 'babel-polyfill';
import Inputmask from 'inputmask';
import masks from './masks';
import {
    MASKED_PHONE_FIELD_ID,
    MASKED_POSTAL_FIELD_ID,
    TESTING_PARAM_CC_URL,
    HELPER_PHONE_LABEL_CLASS,
    HELPER_POSTAL_LABEL_CLASS,
    COMMON_PAGE,
    PRODUCT_REGISTRATION_PAGE,
    VIEW_MODE_ADDRESS_FIELD_SELECTOR,
    PRODUCT_REGISTRATION_COUNTRY_FILED_ATTR,
    VIEW_MODE_COUNTRY_CODE_FIELD_SELECTOR,
    PRODUCT_REGISTRATION_PHONE_FIELD_CLASS,
    PRODUCT_REGISTRATION_POSTAL_FIELD_CLASS,
    PRODUCT_REGISTRATION_HELPER_LABEL_CLASS,
    PRODUCT_REGISTRATION_HELPER_POSTAL_LABEL_CLASS,
    VIEW_MODE,
    MASKED_VAT_FIELD_ID,
    HELPER_VAT_LABEL_CLASS,
    COUNTRY_NOT_RECOGNIZED,
} from './constants';
import { PostalMask } from './masks/postal.mask';
import { PhoneMask } from './masks/phone.mask';
import { VatMask } from './masks/vat.mask';
import { excludePoBox } from './validators/po-box.validator';

/**
 * Add validation method for jquery.validation plugin
 */
$.validator.addMethod('maskphonenumber', PhoneMask.maskMethod, '');
$.validator.addMethod('maskpostalnumber', PostalMask.maskMethod, '');
$.validator.addMethod('maskvatnumber', VatMask.maskMethod, '');
$.validator.addMethod('pobox', excludePoBox, '');

// Resetting global definitions to support number placeholders
// ( number 9 is missing in country code placeholder as it's a mask definition )
// can be done by escaping nines in the phone masks, but is cleaner with this
// approach
Inputmask.prototype.definitions = {};

// Set definition for static method format

export default class MaskValidation {
    constructor(countryCode, masksConfig) {
        this.countryCode = this.getCountryCode(countryCode);
        this.masksList = masks[this.countryCode.toUpperCase()] ? masksConfig : [];
    }

    init(messageFormatter = x => x) {
        if (this.countryCode === COUNTRY_NOT_RECOGNIZED) {
            return;
        }
        this.masksList.forEach(config => {
            const el = $(config.selector).get(0);
            if (!el) {
                console.warn(`Mask input element ${config.selector} is not found`);
                return;
            }
            const maskInstance = new config.maskConstructor({
                el,
                countryCode: this.countryCode,
            });

            maskInstance
                .setValidationMessage()
                .addHelperMessages(config.helperLabelClassName, messageFormatter)
                .initMask();
        });
    }

    getCountryCode(countryCode) {
        const countryCodeFromURL = this.getParamFromUrl(TESTING_PARAM_CC_URL);
        return countryCodeFromURL || countryCode;
    }

    /**
     * Get parameter value (e.g. countryCode)  from URL (for auto tests)
     * @param {string} param - param from URL
     */
    getParamFromUrl(param) {
        const query = window.location.search.substring(1);
        const vars = query.split('&');

        for (let i = 0; i < vars.length; i++) {
            const pair = vars[i].split('=');
            if (pair[0] === param) {
                return pair[1].toUpperCase();
            }
        }

        return false;
    }

    adjustView(element) {
        this.masksList.forEach(maskConfig => {
            maskConfig.maskConstructor.adjustView(this.countryCode, element);
        });
    }
}

function maskValidationLegacyAdapter(
    validationPage,
    event,
    phoneElementId = MASKED_PHONE_FIELD_ID,
    postalId = MASKED_POSTAL_FIELD_ID,
    customCountryCode,
    isPostalMaskNeeded,
    vatElementId = MASKED_VAT_FIELD_ID,
) {
    /**
     * Split string by symbol
     * @param str -
     * @param symbol -
     * @returns {Array|null} -
     */
    function splitBySymbol(str, symbol) {
        return str ? str.split(symbol) : null;
    }

    /**
     *
     * @param {string} element -
     * @returns {string} - countryCode
     */
    function getCountryCodeOnProductRegistration(element) {
        const selectedOption = $(element).find('option:selected')[0];
        const dataAttrValue = selectedOption.getAttribute(PRODUCT_REGISTRATION_COUNTRY_FILED_ATTR);
        const countryCode = splitBySymbol(dataAttrValue, '_');
        return countryCode ? countryCode[1].toUpperCase() : COUNTRY_NOT_RECOGNIZED;
    }

    if (validationPage === COMMON_PAGE) {
        const appliedMasks = [
            {
                helperLabelClassName: HELPER_PHONE_LABEL_CLASS,
                selector: phoneElementId,
                maskConstructor: MaskValidation.maskClasses.phone,
                countryCodeDataAttrName: 'data-rule-maskphonenumber',
            },
            isPostalMaskNeeded && {
                helperLabelClassName: HELPER_POSTAL_LABEL_CLASS,
                selector: postalId,
                maskConstructor: MaskValidation.maskClasses.postal,
                countryCodeDataAttrName: 'data-rule-maskpostalnumber',
            },
            {
                helperLabelClassName: HELPER_VAT_LABEL_CLASS,
                selector: vatElementId,
                maskConstructor: MaskValidation.maskClasses.vat,
                countryCodeDataAttrName: 'data-rule-maskvatnumber',
            },
        ].filter(Boolean);

        // Get country code from first available mask element
        const countryCodeFromAttr = appliedMasks
            .map(cfg => $(cfg.selector).attr(cfg.countryCodeDataAttrName) || null)
            .filter(Boolean)[0];

        if (!countryCodeFromAttr) {
            throw new Error('Cannot get country code from data attribute');
        }

        const validator = new MaskValidation(countryCodeFromAttr, appliedMasks);
        validator.init();
    }

    if (validationPage === PRODUCT_REGISTRATION_PAGE) {
        let element = $('.country-list');
        if (event) {
            element = event.target;
        }
        const countryCode = getCountryCodeOnProductRegistration(element);
        const appliedMasks = [
            {
                helperLabelClassName: PRODUCT_REGISTRATION_HELPER_LABEL_CLASS,
                selector: PRODUCT_REGISTRATION_PHONE_FIELD_CLASS,
                maskConstructor: MaskValidation.maskClasses.phone,
            },
            isPostalMaskNeeded && {
                helperLabelClassName: PRODUCT_REGISTRATION_HELPER_POSTAL_LABEL_CLASS,
                selector: PRODUCT_REGISTRATION_POSTAL_FIELD_CLASS,
                maskConstructor: MaskValidation.maskClasses.postal,
            },
        ].filter(Boolean);
        const validator = new MaskValidation(countryCode, appliedMasks);
        validator.init(message => `(${message})`);
    }
    if (validationPage === VIEW_MODE) {
        const elements = Array.prototype.slice.apply(
            document.querySelectorAll(VIEW_MODE_ADDRESS_FIELD_SELECTOR),
        );
        const appliedMasks = [
            {
                helperLabelClassName: HELPER_PHONE_LABEL_CLASS,
                selector: phoneElementId || MASKED_PHONE_FIELD_ID,
                maskConstructor: MaskValidation.maskClasses.phone,
            },
            {
                helperLabelClassName: HELPER_POSTAL_LABEL_CLASS,
                selector: postalId || MASKED_POSTAL_FIELD_ID,
                maskConstructor: MaskValidation.maskClasses.postal,
            },
            {
                helperLabelClassName: HELPER_PHONE_LABEL_CLASS,
                selector: vatElementId,
                maskConstructor: MaskValidation.maskClasses.vat,
            },
        ];
        elements.forEach(element => {
            const countryCode = $(element)
                .find(VIEW_MODE_COUNTRY_CODE_FIELD_SELECTOR)
                .text();
            const validator = new MaskValidation(countryCode, appliedMasks);
            validator.adjustView(element);
        });
    }
}

MaskValidation.maskClasses = {
    phone: PhoneMask,
    postal: PostalMask,
    vat: VatMask,
};

MaskValidation.legacy = maskValidationLegacyAdapter;

window.MaskValidation = MaskValidation;

// if (process.env.NODE_ENV !== 'production') {
document.addEventListener('DOMContentLoaded', () => {
    MaskValidation.legacy('VIEW');
    MaskValidation.legacy('COMMON', '', MASKED_PHONE_FIELD_ID, '', '', true, MASKED_VAT_FIELD_ID);
    const dropdown = document.getElementById('vat-dropdown');

    if (dropdown === null) return;

    dropdown.addEventListener('change', event => {
        const inputElement = document.getElementById('txtVatId');
        inputElement.setAttribute('data-config-postfix', event.target.value);
        // const value = inputElement.value;
        const clonedInputElement = inputElement.cloneNode(true);
        inputElement.parentElement.replaceChild(clonedInputElement, inputElement);
        // eslint-disable-next-line no-new
        clonedInputElement.focus();
        new MaskValidation(inputElement.dataset.ruleMaskvatnumber, [
            {
                helperLabelClassName: '.vat-format',
                selector: '#txtVatId',
                maskConstructor: MaskValidation.maskClasses.vat,
            },
        ]).init();

        if (document.getElementById('txtVatId-error')) {
            const errorPopup = document.getElementById('txtVatId-error').parentElement;
            errorPopup.parentElement.removeChild(errorPopup);

            clonedInputElement.classList.remove('error');
        }
    });
});
// }
